import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import { Button, Grid, InputAdornment, TextField } from "@material-ui/core";

import Autocomplete from "@material-ui/lab/Autocomplete";

import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";

import InfoTooltip from "src/components/InfoTooltip";
import { itemOptionsSelector } from "src/store/itemOptions/selector";
import { inventoriesSelector } from "src/store/inventory/selector";

import { fetchItems as fetchItemOptions } from "src/store/itemOptions/actions";

import { setActiveDialog } from "src/store/adminHtml/actions";
import { clearUpdateMethod } from "src/store/common";

import type { Inventory } from "src/interfaces";
import { METHOD_TYPE } from "src/enums";
import VendorField from "src/components/VendorField";
import NumberField from "src/components/NumberField";
import { bankAccountsSelector } from "src/store/bankAccount/selector";

const initFormValues: Inventory = {
  id: "",
  brand: "",
  category: "",
  department: "",
  item_title: "",
  location: "",
  notes: "",
  platforms_listed: [],
  sku: "",
  sub_category: "",
  purchase_price: 0,
  purchase_date: undefined,
  list_date: undefined,
  quantity: 1,
};

const AddMatchingInventory = ({ goBack, onSave, saleValues, render }: any) => {
  const dispatch = useDispatch();
  const { items: itemOptions, loading: itemOptionsLoading }: any =
    useSelector(itemOptionsSelector);
  const {
    loading,
    loaded,
    method,
    error: apiError,
  }: any = useSelector(inventoriesSelector);

  const [values, setValues] = useState<Inventory>(initFormValues);

  const [departments, setDepartments] = useState<Array<string>>([]);
  const [categories, setCategories] = useState<Array<string>>([]);
  const [subCategories, setSubCategories] = useState<Array<string>>([]);
  const bankAccounts = useSelector(bankAccountsSelector);
  const allPlatforms = useMemo(() => {
    const salePlatforms = bankAccounts.reduce((sp, ba) => {
      if (!ba.hidden && ba.type === "sale-platform") sp.push(ba.name);
      return sp;
    }, [] as string[]);
    salePlatforms.sort((a, b) => a.localeCompare(b));
    return salePlatforms;
  }, [bankAccounts]);

  useEffect(() => {
    if (!itemOptionsLoading && (!itemOptions || !itemOptions.length)) {
      dispatch(fetchItemOptions({}));
    }
  }, []);

  useEffect(() => {
    if (loaded && !apiError && method === METHOD_TYPE.CREATE) {
      dispatch(clearUpdateMethod());
      dispatch(setActiveDialog(""));
    }
  }, [loaded]);

  // useEffect(() => {
  //   if (apiError && !loading && (method == METHOD_TYPE.CREATE)) {
  //     toast.error(apiError);
  //   }
  // }, [apiError]);

  useEffect(() => {
    updateCategories();
  }, [values.department]);

  useEffect(() => {
    updateSubCategories();
  }, [values.category]);

  useEffect(() => {
    if (itemOptions && itemOptions.length) {
      setSubCategories([]);
      setCategories([]);
      const data = itemOptions
        .map((option: any) => option["Department"])
        .filter((d, i) => {
          const j = itemOptions.findIndex((e) => e["Department"] === d);
          return i === j;
        })
        .sort();
      setDepartments(data);
    }
  }, [itemOptions]);

  useEffect(() => {
    setValues({ ...initFormValues, ...saleValues });
  }, [saleValues]);

  const updateSubCategories = () => {
    let data = itemOptions
      .filter(
        (option: any) =>
          option.Department === values.department &&
          option.Category === values.category
      )
      .map((option: any) => option["Sub-Category"]);
    data = data
      .filter((e, i) => {
        const j = data.indexOf(e);
        return i === j;
      })
      .sort();
    setSubCategories(data);
    if (data.indexOf(values.sub_category) === -1) {
      setValues({ ...values, sub_category: "" });
    }
  };

  const updateCategories = () => {
    let data = itemOptions
      .filter((option: any) => option.Department === values.department)
      .map((option: any) => option["Category"]);
    data = data
      .filter((e, i) => {
        const j = data.indexOf(e);
        return i === j;
      })
      .sort();
    setSubCategories([]);
    setCategories(data);
    if (data.indexOf(values.category) === -1) {
      setValues({ ...values, category: "" });
    }
  };

  const handleChange = (prop: keyof Inventory) => (event, t?: any) => {
    let value = event.target.value;
    if ("department" === prop) {
      value = departments[Number(value)];
    } else if ("category" === prop) {
      value = categories[Number(value)];
    } else if ("sub_category" === prop) {
      value = subCategories[Number(value)];
    } else if ("platforms_listed" === prop) {
      value = t;
    } else if ("purchase_price" === prop) {
      value = +value || 0;
    }
    setValues({ ...values, [prop]: value });
  };

  const renderOption = (option, { inputValue }) => {
    const matches = match(option, inputValue);
    const parts = parse(option, matches);

    return (
      <div>
        {parts.map((part, index) => (
          <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
            {part.text}
          </span>
        ))}
      </div>
    );
  };

  const save = () => {
    onSave(values);
    goBack();
  };

  return render({
    title: "Add Inventory Information",
    main: (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} className="flex justify-end">
          <TextField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            label="Item Title"
            value={values.item_title}
            disabled={loading}
            onChange={handleChange("item_title")}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip text="Source where you purchased the item." size={18} />
          <VendorField
            className="w-11/12"
            margin="dense"
            disabled={loading}
            value={values.vendor}
            onChange={(vendor) => {
              setValues((values) => ({
                ...values,
                vendor: vendor || "",
              }));
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="Departments help to organize your inventory, they are the largest grouping to set inventory/sales apart."
            size={18}
          />
          <Autocomplete
            disabled={itemOptionsLoading}
            options={departments}
            value={values.department}
            className="w-11/12"
            autoHighlight
            onChange={(_e, value) => {
              setValues({ ...values, department: value || "" });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Department"
                className="w-full"
                variant="outlined"
              />
            )}
            renderOption={renderOption}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="Categories help to organize your inventory, they are the second largest grouping to set inventory/sales apart."
            size={18}
          />
          <Autocomplete
            disabled={itemOptionsLoading}
            options={categories}
            value={values.category}
            autoHighlight
            className="w-11/12"
            onChange={(_e, value) => {
              setValues({ ...values, category: value || "" });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Category"
                className="w-full"
                variant="outlined"
              />
            )}
            renderOption={renderOption}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="Sub-categories help to organize your inventory, they are the most specific grouping to set inventory/sales apart."
            size={18}
          />
          <Autocomplete
            disabled={itemOptionsLoading}
            options={subCategories}
            value={values.sub_category}
            autoHighlight
            className="w-11/12"
            onChange={(_e, value) => {
              setValues({ ...values, sub_category: value || "" });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Sub category"
                className="w-full"
                variant="outlined"
              />
            )}
            renderOption={renderOption}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-end">
          <TextField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            label="Brand"
            value={values.brand}
            disabled={loading}
            onChange={handleChange("brand")}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip text="Where you have stored the item." size={18} />
          <TextField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            label="Location"
            value={values.location}
            disabled={loading}
            onChange={handleChange("location")}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-end">
          <TextField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            label="SKU"
            value={values.sku}
            disabled={loading}
            onChange={handleChange("sku")}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="The amount you paid for the inventory."
            size={18}
          />
          <NumberField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            label="Purchase Price"
            value={values.purchase_price || ""}
            disabled={loading}
            onChange={handleChange("purchase_price")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-end">
          <Autocomplete
            multiple
            className="w-11/12"
            options={allPlatforms}
            value={values.platforms_listed ? values.platforms_listed : []}
            onChange={handleChange("platforms_listed")}
            autoHighlight
            renderOption={renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Platforms Listed"
                variant="outlined"
                className="w-full"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="Enter any other information that you need in here."
            size={18}
          />
          <TextField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            label="Notes"
            value={values.notes}
            disabled={loading}
            onChange={handleChange("notes")}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="The date that you purchased this item."
            size={18}
          />
          <ZonedKeyboardDatePicker
            disableToolbar
            className="w-11/12"
            inputVariant="outlined"
            format="MM/dd/yyyy"
            margin="dense"
            label="Purchase Date"
            autoOk={true}
            required
            value={values.purchase_date || null}
            disabled={loading}
            onChange={(d) => {
              if (!d) return;
              setValues({ ...values, purchase_date: d });
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="The date that you listed this item for sale."
            size={18}
          />
          <ZonedKeyboardDatePicker
            disableToolbar
            className="w-11/12"
            inputVariant="outlined"
            format="MM/dd/yyyy"
            margin="dense"
            label="List Date"
            autoOk={true}
            value={values.list_date || null}
            disabled={loading}
            onChange={(d) => {
              if (!d) return;
              setValues({ ...values, list_date: d });
            }}
            KeyboardButtonProps={{
              "aria-label": "change date",
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} className="flex justify-between">
          <InfoTooltip
            text="The number of these items you’d like to create."
            size={18}
          />
          <TextField
            className="w-11/12"
            variant="outlined"
            margin="dense"
            required
            label="Quantity"
            type="number"
            value={values.quantity}
            disabled={loading}
            onChange={handleChange("quantity")}
            InputProps={{
              inputProps: { min: 1 },
            }}
          />
        </Grid>
      </Grid>
    ),
    actions: (
      <>
        <Button variant="contained" onClick={goBack}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={save}>
          Match
        </Button>
      </>
    ),
  });
};

export default AddMatchingInventory;
